import { D3Selection } from "../draw-calendar";
import moment, { Moment } from "moment";
import { localMonthName } from "util/local-dates";
import {
    CalendarDrawerContent,
    CalendarState,
    DateMetaData,
    ModuleInformation,
} from "burde";
import {
    LayoutPage,
    LayoutFont,
    ModuleEndpoints,
    WeekModuleElement,
    RecurringYear,
    SvgCalendarLayout,
} from "ui/constants";
import { getMoonSvg } from "./moon-phases";
import { elementIdentifiers, getIconIdentifier, colors } from "./constants";
import drawDynamicMonthModule from "./draw-dynamic-month-module";
import * as FlagSvg from "svg/assets/Flag.svg";
import { showRedText, stringIsNotNullOrEmpty } from "./helper";
import { DynamicWeekModuleId, LegacyFonts } from "svg/constants";

type DayModuleElement = {
    slotId: number;
    id: number;
    title: string;
    accentColor: string | null;
    endpoints: ModuleEndpoints;
};

const UpdateTemplateLayout = async (
    template: D3Selection,
    Header_WeekNumberPrefix: string,
    date: Moment,
    layout: LayoutPage,
    calendar: CalendarState,
    fontData: LayoutFont | null,
    weekModulePlaceholders: boolean,
    dayModuleElements: DayModuleElement[] | null,
    weekModuleElements: WeekModuleElement[] | null,
    calendarDrawerContent: CalendarDrawerContent,
) => {
    updateWeekText(template, `${Header_WeekNumberPrefix} ${date.format("W")}`);

    updateMonthText(template, date);
    updateYearText(template, date);

    // Iterate through predefined number of days.
    for (let i = 0; i < layout.numberOfDays; i++) {
        // Create cloned date for use in iteration.
        const iterationDate = date.clone();

        // Select day element in template with predefined id structure.
        const dayElement = template.select(`${elementIdentifiers.day}${i + 1}`);

        const dayNumber = dayElement.attr("data-day");

        iterationDate.add(Number(dayNumber) - 1, "days");

        updateDayDateText(dayElement, iterationDate);
        updateDayNameText(dayElement, iterationDate);

        // Adds personal date if match is found in current iteration.
        updatePersonalDateText(dayElement, iterationDate, calendar);

        // Set line style
        updateDayLines(dayElement, calendar);

        const maxDayModules = calendarDrawerContent.layout
            ? calendarDrawerContent.layout.maxDayModules
            : 0;

        if (dayModuleElements !== null) {
            updateDayModules(
                dayElement,
                dayModuleElements,
                maxDayModules,
                calendar.dayModules,
            );
        }

        updateDaySettings(
            dayElement,
            iterationDate,
            calendar,
            calendarDrawerContent,
        );
    }

    if (fontData) {
        updateFonts(template, fontData, calendar.fontSetName);
    }

    if (weekModulePlaceholders) {
        updateWeekModulePlaceholders(template, calendarDrawerContent.layout);
    }

    if (weekModuleElements !== null) {
        updateWeekModules(
            template,
            weekModuleElements,
            date,
            calendarDrawerContent.metadata,
            calendar.culture,
            Header_WeekNumberPrefix,
            calendar.weekModules,
        );
    }
};

const updateDaySettings = (
    dayElement: D3Selection,
    date: Moment,
    calendar: CalendarState,
    calendarDrawerContent: CalendarDrawerContent,
) => {
    const { optionalDaySettings } = calendar;
    const { metadata } = calendarDrawerContent;

    const dateMetadata = metadata.find(
        (d) => d.date === date.format("YYYYMMDD"),
    );

    if (showRedText(date, dateMetadata, calendar.culture)) {
        let accentColor = colors.redDay;

        if (
            calendarDrawerContent.layout &&
            calendarDrawerContent.layout.accentColor
        ) {
            accentColor = calendarDrawerContent.layout.accentColor;
        }

        setRedDayAccentColor(dayElement, accentColor);
    }

    const {
        showTimestamps,
        showMoonPhases,
        showRedDays,
        showNameDays,
        showOtherDays,
        showFlagDays,
    } = optionalDaySettings;

    if (showTimestamps) {
        addTimestamps(dayElement);
    }

    if (dateMetadata === undefined) {
        return;
    }

    const {
        moonphase,
        red_days,
        evenings,
        nameday,
        other_holidays,
        flagdays,
    } = dateMetadata;

    SetIcons(showFlagDays, flagdays, dayElement, showMoonPhases, moonphase);

    SetMetadataLines(
        date,
        showNameDays,
        showOtherDays,
        showRedDays,
        nameday,
        dayElement,
        red_days,
        evenings,
        other_holidays,
    );
};

const SetIcons = (
    showFlagDays: boolean,
    flagdays: string,
    dayElement: D3Selection,
    showMoonPhases: boolean,
    moonphase: string,
) => {
    const showFlagDay = showFlagDays && stringIsNotNullOrEmpty(flagdays);
    const showMoonPhase = showMoonPhases && stringIsNotNullOrEmpty(moonphase);

    let currentIconPosition = 1;

    if (showFlagDay) {
        addFlagDay(dayElement, currentIconPosition);
        currentIconPosition++;
    }

    if (showMoonPhase) {
        addMoonPhase(dayElement, moonphase, currentIconPosition);
        currentIconPosition++;
    }
};

const SetMetadataLines = (
    date: Moment,
    showNameDays: boolean,
    showOtherDays: boolean,
    showRedDays: boolean,
    nameday: string,
    dayElement: D3Selection,
    red_days: string,
    evenings: string,
    other_holidays: string,
) => {
    const showRedDay = showRedDays && stringIsNotNullOrEmpty(red_days);
    const showEvening = showRedDays && stringIsNotNullOrEmpty(evenings);
    const showOtherDay =
        showOtherDays && stringIsNotNullOrEmpty(other_holidays);

    // Starts as one since first meta line is reserved for name days.
    let currentMetadataLines = 1;

    if (showNameDays && nameday) {
        addNameDay(dayElement, nameday);
    }

    if (showRedDay) {
        addRedDayText(dayElement, red_days);

        currentMetadataLines++;
    }

    if (showEvening) {
        const identifier =
            currentMetadataLines === 1
                ? elementIdentifiers.metaRowTwo
                : elementIdentifiers.metaRowThree;

        addEveningText(dayElement, evenings, identifier);

        currentMetadataLines++;
    }

    if (showOtherDay) {
        const identifier =
            currentMetadataLines === 1
                ? elementIdentifiers.metaRowTwo
                : elementIdentifiers.metaRowThree;

        addOtherDayText(dayElement, other_holidays, identifier);
    }
};

const addNameDay = (dayElement: D3Selection, name: string) => {
    if (name !== "") {
        dayElement
            .select(elementIdentifiers.metaRowOne)
            .select("tspan")
            .attr("fill", colors.nameDay)
            .text(name);
    }
};

const addTimestamps = (dayElement: D3Selection) => {
    const timestampsElement = dayElement.select(elementIdentifiers.timestamps);
    // Change style display to show timestamps.
    timestampsElement.style("display", null);
};

const addMoonPhase = (
    dayElement: D3Selection,
    moonPhase: string,
    iconPosition: number,
) => {
    const moonSvg = getMoonSvg(moonPhase);

    if (moonSvg) {
        const identifier = getIconIdentifier(iconPosition);

        dayElement
            .select(identifier)
            .html(moonSvg)
            .select("svg")
            .attr("width", "50")
            .attr("height", "50");
    }
};

const addFlagDay = (dayElement: D3Selection, iconPosition: number) => {
    const identifier = getIconIdentifier(iconPosition);

    dayElement
        .select(identifier)
        .html(FlagSvg)
        .select("svg")
        .attr("width", "50")
        .attr("height", "50");
};

const setRedDayAccentColor = (dayElement: D3Selection, accentColor: string) => {
    //change color to red
    dayElement.select(elementIdentifiers.dateNumber).attr("fill", accentColor);
    dayElement.select(elementIdentifiers.dayName).attr("fill", accentColor);
};

const addRedDayText = (dayElement: D3Selection, redDay: string) => {
    //add red day text
    dayElement
        .select(elementIdentifiers.metaRowTwo)
        .select("tspan")
        .attr("fill", colors.redDay)
        .text(redDay);
};

const addEveningText = (
    dayElement: D3Selection,
    eveningText: string,
    metaRowIdentifier: string,
) => {
    //add evening text
    dayElement.select(metaRowIdentifier).select("tspan").text(eveningText);
};

const addOtherDayText = (
    dayElement: D3Selection,
    otherDayText: string,
    metaRowIdentifier: string,
) => {
    //add other holiday text
    dayElement.select(metaRowIdentifier).select("tspan").text(otherDayText);
};

const updateDayDateText = (element: D3Selection, date: Moment) => {
    const dateText = date.format("D");

    element
        .select(elementIdentifiers.dateNumber)
        .select("tspan")
        .text(dateText);
};

const updateDayNameText = (element: D3Selection, date: Moment) => {
    const dayText = date.format("dddd");

    element
        .select(elementIdentifiers.dayName)
        .select("tspan")
        .text(dayText.charAt(0).toUpperCase() + dayText.slice(1));
};

const updateDayLines = (element: D3Selection, calendar: CalendarState) => {
    const dayLineContainer = element.select(elementIdentifiers.dayLines);

    if (calendar.lines == "Dotted") {
        dayLineContainer
            .selectAll("line")
            .attr("stroke-dasharray", "5,5")
            .attr("stroke-width", "1");
    } else if (calendar.lines == "None") {
        dayLineContainer
            .selectAll("line")
            .attr("stroke-dasharray", "")
            .attr("stroke-width", "0");
    } else {
        dayLineContainer
            .selectAll("line")
            .attr("stroke-dasharray", "")
            .attr("stroke-width", "1");
    }
};

const updateFonts = (
    template: D3Selection,
    fontData: LayoutFont,
    fontSetName: string | null,
) => {
    let fontFamily = fontData ? fontData["font-family"] : "";

    if (fontSetName) {
        const legacyFont = LegacyFonts.find((x) => x.title === fontSetName);

        if (legacyFont) {
            fontFamily = legacyFont["font-family"];
        }
    }

    template
        .selectAll(elementIdentifiers.dynamicText)
        .selectAll("tspan")
        .style("font-family", fontFamily);
};

const updateWeekText = (template: D3Selection, text: string) => {
    template.select(elementIdentifiers.weekNumber).select("tspan").text(text);
};

const updateMonthText = (template: D3Selection, date: Moment) => {
    const monthText = getMonthText(date);

    if (!monthText) return;

    template
        .select(elementIdentifiers.monthName)
        .select("tspan")
        .text(monthText);
};

const updateYearText = (template: D3Selection, date: Moment) => {
    template
        .selectAll(elementIdentifiers.monthYear)
        .select("tspan")
        .text(date.year());
};

const getMonthText = (date: Moment) => {
    if (!date.isValid()) {
        return null;
    }

    const shortNames = moment.monthsShort();
    const endOfWeek = date.clone().add(6, "days");

    const sameYear = date.year() === endOfWeek.year();
    const sameMonth = date.month() === endOfWeek.month();

    const currentMonthString =
        shortNames[date.month()].charAt(0).toUpperCase() +
        shortNames[date.month()].substring(1);

    const nextMonthString =
        shortNames[endOfWeek.month()].charAt(0).toUpperCase() +
        shortNames[endOfWeek.month()].substring(1);

    let monthYearText = sameMonth
        ? `${localMonthName(date)}`
        : sameYear
        ? `${currentMonthString}/${nextMonthString}`
        : `${currentMonthString}/${nextMonthString}`;

    return monthYearText;
};

const updatePersonalDateText = (
    element: D3Selection,
    date: Moment,
    calendar: CalendarState,
) => {
    if (!calendar || !calendar.personalDateItems.length) {
        return;
    }

    const matches = calendar.personalDateItems.filter((d) => {
        // TODO (JH): Year is always recurring for newly created dates. Edit: Removed
        // backwards compatibility for older dates so they too are recurring (JH, 2022-03-09).
        // Also NOTE: moment().isSame() has undefined behavior and should not be used!
        return d.date === date.format("MM-DD");
    });

    if (matches.length < 1) {
        return;
    }

    const selectableIcons = [
        { name: "fa-birthday-cake", unicode: "\uf1fd" },
        { name: "fa-heart", unicode: "\uf004" },
        { name: "fa-plane", unicode: "\uf072" },
        { name: "fa-smile", unicode: "\uf118" },
        { name: "fa-frown", unicode: "\uf119" },
    ];

    matches.forEach((match) => {
        const icon = selectableIcons.filter((x) => x.name === match.icon)[0];

        const personalDateTextElement = element
            .select(elementIdentifiers.personalDate)
            .select(elementIdentifiers.personalDateText);

        personalDateTextElement
            .append("tspan")
            .attr("class", `fas`)
            .text(icon ? icon.unicode : "");

        personalDateTextElement.append("tspan").text(match.text).attr("dx", 10);
    });
};

const updateDayModules = (
    dayElementSelection: D3Selection,
    dayModuleElements: DayModuleElement[],
    maxDayModules: number,
    dayModuleInformation: ModuleInformation[],
) => {
    const dayModuleElementSelection = dayElementSelection.select(
        elementIdentifiers.dayModules,
    );

    for (let i = 0; i < dayModuleInformation.length; i++) {
        const dayModule = dayModuleInformation[i];

        const dayModuleElement = dayModuleElements.find(
            (x) => x.id === dayModule.id,
        );

        if (dayModuleElement === undefined) {
            return;
        }

        if (dayModule.slotId >= maxDayModules) {
            continue;
        }

        // Select placeholder element for day module.
        const dayModulePlaceholderIdentifier = `.dayModule${
            dayModule.slotId + 1
        }`;

        const dayModulePlaceholder = dayModuleElementSelection.select(
            dayModulePlaceholderIdentifier,
        );

        if (dayModulePlaceholder) {
            // Get size and position from placeholder element
            const height = dayModulePlaceholder.attr("height");
            const width = dayModulePlaceholder.attr("width");
            const transform = dayModulePlaceholder.attr("transform");

            if (
                dayModuleElement &&
                dayModuleElement.endpoints &&
                dayModuleElement.endpoints.svgData
            ) {
                dayModuleElementSelection
                    .append("g")
                    .attr("transform", transform)
                    .html(<string>dayModuleElement.endpoints.svgData)
                    .select("svg")
                    .attr("width", width)
                    .attr("height", height);
            }
        }
    }
};

const updateWeekModulePlaceholders = (
    template: D3Selection,
    calendarLayout: SvgCalendarLayout | null,
) => {
    if (calendarLayout === null) return;

    for (let i = 1; i <= calendarLayout.maxWeekModules; i++) {
        // Select week module container element
        const weekModuleElement = template.select(
            `${elementIdentifiers.weekModule}${i}`,
        );

        if (weekModuleElement.empty()) {
            continue;
        }

        // Select placeholder element
        const weekModulePlaceholder = template.select(
            `${elementIdentifiers.weekModule}${i}-placeholder`,
        );

        // Get size and position data
        const height = weekModulePlaceholder.attr("height");
        const width = weekModulePlaceholder.attr("width");
        const transform = weekModulePlaceholder.attr("transform");

        const weekModuleNumber = weekModuleElement
            .append("g")
            .attr("class", "week-module-number")
            .attr("transform", transform);

        weekModuleNumber
            .append("text")
            .attr("x", parseInt(width) / 2)
            .attr("y", parseInt(height) / 2)
            .attr("text-anchor", "middle")
            .attr("alignment-baseline", "middle")
            .attr("font-size", "300")
            .attr("text-anchor", "middle")
            .attr("alignment-baseline", "middle")
            .attr("font-weight", 900)
            .attr("fill", "lightgray")
            .attr("font-family", "Quicksand")
            .text(i);
    }
};

const updateWeekModules = (
    template: D3Selection,
    weekModuleElements: WeekModuleElement[],
    date: Moment,
    metaData: DateMetaData[],
    culture: string | null,
    Header_WeekNumberPrefix: string,
    weekModuleInformation: ModuleInformation[],
) => {
    weekModuleInformation.map((weekModule) => {
        const weekModuleElement = weekModuleElements.find(
            (x) => x.id === weekModule.id,
        );

        if (weekModuleElement === undefined) {
            return;
        }

        const position = weekModule.slotId + 1;

        // Select week module container element
        const weekModuleElementSelection = template.select(
            `${elementIdentifiers.weekModule}${position}`,
        );

        // Select placeholder element
        const weekModulePlaceholder = template.select(
            `${elementIdentifiers.weekModule}${position}-placeholder`,
        );

        if (weekModuleElementSelection.empty()) {
            return;
        }

        // Get size and position data
        const height = weekModulePlaceholder.attr("height");
        const width = weekModulePlaceholder.attr("width");
        const transform = weekModulePlaceholder.attr("transform");

        if (
            weekModuleElement &&
            weekModuleElement.endpoints &&
            weekModuleElement.endpoints.svgData
        ) {
            // Append module to container
            weekModuleElementSelection
                .append("g")
                .attr("transform", transform)
                .html(<string>weekModuleElement.endpoints.svgData)
                .select("svg")
                .attr("width", width)
                .attr("height", height);
        }
        // Remove placeholder element
        weekModulePlaceholder.remove();
        // Remove the week module number
        weekModuleElementSelection.select(".week-module-number").remove();
        // If this module is the dynamic month module, draw it.
        if (weekModuleElement.id === DynamicWeekModuleId) {
            // Important to make sure the date is cloned.
            drawDynamicMonthModule(
                weekModuleElementSelection,
                date.clone(),
                metaData,
                culture,
                Header_WeekNumberPrefix,
            );
        }
    });
};

export default UpdateTemplateLayout;
